import { Trans } from "@lingui/macro";
import {
  SectionContainer,
  SectionTitleHeading
} from "@silversea-ssc/ui-components-react";
import {
  SectionTitle,
  PartialSanityImageType
} from "@silversea-ssc/ui-components-react";
import Slider, { SliderSlide } from "components/Slider";
import React, { useState } from "react";
import { HotelOrLandProgrammeFragment } from "types/graphql-types";
import PrePostProgrammeCard from "./PrePostProgrammeCard";
import PrePostProgrammeModal from "./PrePostProgrammeModal";

interface PrePostProgrammes {
  preFallbackImage: PartialSanityImageType | undefined;
  postFallbackImage: PartialSanityImageType | undefined;
  preProgrammes: HotelOrLandProgrammeFragment[] | undefined;
  postProgrammes: HotelOrLandProgrammeFragment[] | undefined;
}

const PrePostProgrammesSlider: React.FC<
  PrePostProgrammes & {
    setOpenedModal: (program: HotelOrLandProgrammeFragment) => void;
  }
> = ({
  preProgrammes = [],
  postProgrammes = [],
  setOpenedModal,
  preFallbackImage,
  postFallbackImage
}) => {
  return (
    <Slider variantColor="white">
      {preProgrammes.map((program) => {
        const fallBackImage =
          program.__typename == "SanityLandProgramme"
            ? preFallbackImage
            : undefined;
        return (
          <SliderSlide key={`pre-${program._key || program._id}`}>
            <PrePostProgrammeCard
              title={program.name}
              image={program.image || fallBackImage}
              duration={
                program.__typename == "SanityLandProgramme"
                  ? program.duration
                  : undefined
              }
              onClick={() => setOpenedModal(program)}
            />
          </SliderSlide>
        );
      })}
      {postProgrammes.map((program) => {
        const fallBackImage =
          program.__typename == "SanityLandProgramme"
            ? postFallbackImage
            : undefined;
        return (
          <SliderSlide key={`post-${program._key || program._id}`}>
            <PrePostProgrammeCard
              title={program.name}
              image={program.image || fallBackImage}
              onClick={() => setOpenedModal(program)}
            />
          </SliderSlide>
        );
      })}
    </Slider>
  );
};

const MemorizedPrePostProgrammesSlider = React.memo(PrePostProgrammesSlider);

const PrePostProgrammes: React.FC<PrePostProgrammes> = ({
  preFallbackImage,
  postFallbackImage,
  preProgrammes = [],
  postProgrammes = []
}) => {
  const [openedModal, setOpenedModal] = useState<
    HotelOrLandProgrammeFragment | undefined
  >(undefined);

  if (preProgrammes.length == 0 && postProgrammes.length == 0) return null;

  return (
    <SectionContainer
      spacingTop="regular"
      spacingBottom="regular"
      backgroundColor="destinationLight"
      dataContentName="section_pre-post-hotels"
    >
      <SectionTitle variant="wide">
        <SectionTitleHeading>
          <Trans>Pre & Post Cruise Extensions And Hotels</Trans>
        </SectionTitleHeading>
      </SectionTitle>

      <MemorizedPrePostProgrammesSlider
        preProgrammes={preProgrammes}
        postProgrammes={postProgrammes}
        setOpenedModal={setOpenedModal}
        preFallbackImage={preFallbackImage}
        postFallbackImage={postFallbackImage}
      />

      {openedModal && (
        <PrePostProgrammeModal
          modal={{
            isOpen: !!openedModal,
            onRequestClose: () => setOpenedModal(undefined)
          }}
          programme={openedModal}
        />
      )}
    </SectionContainer>
  );
};

export default PrePostProgrammes;
